import type { FormField } from '@lucid/lego';
import { validationPatterns } from '@lucid/lego';
import type { BusinessSearchResult } from '../../../../../../api/services/lupp/order/dto.order';

type FinancingAgreementFieldOptions = {
  hasAgreement: boolean;
  onDownload?: () => void;
  downloadErrorMessage?: string | null;
};

/**
 * Creates form fields configuration for Finance Provider Details form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for finance provider fields
 * @param theme - Theme variant (light or dark)
 * @param onFinanceProviderChange - Callback when business name is selected
 * @returns Array of FormField configurations
 */
export const createFinanceProviderFormFields = (
  labels?: Record<string, string>,
  defaultValues?: {
    name?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    vat?: string;
  },
  theme: 'light' | 'dark' = 'dark',
  onFinanceProviderChange?: (business: BusinessSearchResult | null) => void,
  financingAgreementOptions?: FinancingAgreementFieldOptions
): FormField[] => [
  {
    id: 'financeProviderGroup',
    fieldType: 'group',
    title: labels?.['form.financeProviderDetails'] ?? 'Finance Provider Details',
    width: 'full',
    children: [
      {
        id: 'financingAgreement',
        fieldType: 'custom',
        component: 'FinancingAgreementField',
        name: 'financingAgreement',
        apiKey: 'financingAgreement',
        width: 'full',
        componentProps: {
          label: labels?.['form.financingAgreementLabel'] ?? 'Financing Agreement',
          hasAgreement: financingAgreementOptions?.hasAgreement ?? false,
          onDownload: financingAgreementOptions?.onDownload,
          downloadErrorMessage: financingAgreementOptions?.downloadErrorMessage,
          theme: theme === 'dark' ? 'light' : 'dark',
        },
      },
      {
        id: 'name',
        fieldType: 'custom',
        component: 'BusinessNameSearch',
        name: 'name',
        apiKey: 'name',
        required: true,
        width: 'third',
        defaultValue: defaultValues?.name,
        componentProps: {
          label: labels?.['form.financeProviderName'] ?? 'Finance Provider Name',
          placeholder: labels?.['form.searchCompanyPlaceholder'] ?? 'Search for company...',
          required: true,
          disabled: true,
          dataTestId: 'finance-provider-name-search',
          defaultValue: defaultValues?.name
            ? {
                name: defaultValues.name,
                duns: '',
                primaryName: defaultValues.name,
                isOutOfBusiness: false,
                address: defaultValues.address ?? '',
                city: defaultValues.city ?? '',
                postalCode: defaultValues.postalCode ?? '',
                country: '',
                countryCode: '',
                displayText: defaultValues.name,
              }
            : null,
          onChange: onFinanceProviderChange,
          theme: theme === 'dark' ? 'light' : 'dark',
          messages: {
            searchingText: labels?.['form.searching'] ?? 'Searching...',
            apiErrorText:
              labels?.['form.searchUnavailable'] ??
              'Search is temporarily unavailable. Please try again later.',
            minCharsText:
              labels?.['form.minCharsToSearch'] ?? 'Type at least 3 characters to search',
            noResultsText: labels?.['form.noResults'] ?? 'No matching results found.',
            validationRequiredText:
              labels?.['validation.selectValidCompany'] ??
              'Please select a valid Company from the list.',
          },
        },
        validationMessages: {
          required:
            labels?.['form.fieldRequired'] ??
            labels?.['validation.selectValidCompany'] ??
            'Please select a valid Company from the list.',
        },
      },
      {
        id: 'address',
        fieldType: 'text',
        name: 'address',
        apiKey: 'address',
        label: labels?.['form.financeProviderAddress'] ?? 'Address',
        required: true,
        disabled: true,
        width: 'full',
        defaultValue: defaultValues?.address,
        validationMessages: {
          required: labels?.['form.fieldRequired'] ?? 'This field is required',
        },
      },
      {
        id: 'city',
        fieldType: 'text',
        name: 'city',
        apiKey: 'city',
        label: labels?.['form.financeProviderCity'] ?? 'City',
        required: true,
        disabled: true,
        width: 'half',
        defaultValue: defaultValues?.city,
        pattern: validationPatterns.city,
        validationMessages: {
          required: labels?.['form.fieldRequired'] ?? '',
          pattern: labels?.['form.cityNameInvalid'] ?? 'Please enter a valid city name',
        },
      },
      {
        id: 'postalCode',
        fieldType: 'text',
        name: 'postalCode',
        apiKey: 'postalCode',
        label: labels?.['form.financeProviderPostalCode'] ?? 'Postal Code',
        required: true,
        disabled: true,
        width: 'half',
        defaultValue: defaultValues?.postalCode,
        postalCodeAutoPopulate: {
          cityField: 'city',
        },
        validationMessages: {
          required: labels?.['form.fieldRequired'] ?? '',
        },
      },
      {
        id: 'vat',
        fieldType: 'text',
        name: 'vat',
        apiKey: 'vat',
        label: labels?.['form.financeProviderVat'] ?? 'VAT',
        required: true,
        width: 'third',
        defaultValue: defaultValues?.vat,
        validationMessages: {
          required: labels?.['form.fieldRequired'] ?? '',
        },
      },
    ],
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    buttons: [
      {
        id: 'saveButton',
        label: labels?.['form.save'] ?? 'Save',
        type: 'primary',
        size: 'large',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
      },
    ],
  },
];
